import React, { useState, useCallback, useMemo, memo } from "react";
import PropTypes from "prop-types";
import { Tabs, Tab, Box } from "@mui/material";

/**
 * DashboardTabs Component
 * Modern tab component matching original TabComponent styling
 * Memoized to prevent unnecessary re-renders
 *
 * @component
 */
const DashboardTabs = memo(({ tabs, defaultActiveKey, onTabChange }) => {
  const [activeTab, setActiveTab] = useState(defaultActiveKey || tabs[0]?.key);

  // Memoized tab click handler
  const handleTabChange = useCallback(
    (_event, newKey) => {
      setActiveTab(newKey);
      onTabChange?.(newKey);
    },
    [onTabChange]
  );

  // Memoized active content
  const activeContent = useMemo(
    () => tabs.find((tab) => tab.key === activeTab)?.content,
    [tabs, activeTab]
  );

  return (
    <Box sx={{ width: '100%' }}>
      {/* Tab Headers - Purple background with rounded top */}
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="fullWidth"
        TabIndicatorProps={{
          style: { backgroundColor: "#fff", height: 3 },
        }}
        sx={{
          minHeight: '2.313rem',
          backgroundColor: '#3901FF',
          borderRadius: '0.75rem 0.75rem 0 0',
          '& .MuiTabs-flexContainer': {
            height: '2.313rem',
          },
          '& .MuiTab-root': {
            minHeight: '2.313rem',
            height: '2.313rem',
            minWidth: 0,
            padding: '0 0.5rem',
            fontSize: { xs: '0.7rem', md: '0.813rem' },
            lineHeight: 1,
            fontWeight: 600,
            color: 'rgba(255,255,255,0.6)',
            textTransform: 'none',
            letterSpacing: 0,
            whiteSpace: 'nowrap',
            transition: 'color 200ms ease-in-out',
          },
          // Give first two tabs (longer labels) more horizontal space
          '& .MuiTab-root:nth-of-type(1), & .MuiTab-root:nth-of-type(2)': {
            flex: 2,
          },
          '& .MuiTab-root:nth-of-type(3), & .MuiTab-root:nth-of-type(4)': {
            flex: 1,
          },
          '& .Mui-selected': {
            color: '#fff !important',
          },
        }}
      >
        {tabs.map((tab) => (
          <Tab key={tab.key} value={tab.key} label={tab.label} disableRipple />
        ))}
      </Tabs>

      {/* Tab Content */}
      <Box sx={{ backgroundColor: '#fff' }}>
        {activeContent}
      </Box>
    </Box>
  );
});

DashboardTabs.propTypes = {
  /** Array of tab objects with key, label, and content */
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      content: PropTypes.node.isRequired,
    })
  ).isRequired,
  /** Default active tab key */
  defaultActiveKey: PropTypes.string,
  /** Callback when tab changes */
  onTabChange: PropTypes.func,
};

export default DashboardTabs;
